import { Children, cloneElement, isValidElement } from "react";
import type { ReactElement, ReactNode } from "react";

type Props = {
  /** Column ids in display order. */
  order: readonly string[];
  /**
   * The row's cells, in any order. Each cell is identified by a `columnId` prop
   * (header cells) or a `data-column` attribute (body cells); cells without an
   * id, or whose id is not in `order`, are rendered after the ordered ones.
   */
  children: ReactNode;
};

const cellId = (el: ReactElement): string | undefined => {
  const props = el.props as Record<string, unknown>;
  const id = props.columnId ?? props["data-column"];
  return typeof id === "string" ? id : undefined;
};

/**
 * Renders a table row's cells in a user-defined order without the row having
 * to know about it: the row keeps its cells as plain JSX (conditionals and all)
 * and this wrapper re-sorts them by column id. Renders a fragment — no extra DOM.
 */
export default function OrderedCells({ order, children }: Props) {
  const byId = new Map<string, ReactElement>();
  const rest: ReactElement[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const id = cellId(child);
    if (id && !byId.has(id)) byId.set(id, child);
    else rest.push(child);
  });

  const ordered: ReactElement[] = [];
  for (const id of order) {
    const cell = byId.get(id);
    if (!cell) continue;
    byId.delete(id);
    ordered.push(cloneElement(cell, { key: id }));
  }
  // Ids present in the row but missing from `order` (should not happen once the
  // order is reconciled with the column list) keep their original position.
  byId.forEach((cell, id) => ordered.push(cloneElement(cell, { key: id })));
  rest.forEach((cell, i) =>
    ordered.push(cloneElement(cell, { key: cell.key ?? `rest-${i}` })),
  );

  return <>{ordered}</>;
}
